import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function OwnerLogin() {
  const { user, loading, loginWithEmail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [deviceName, setDeviceName] = useState(
    (typeof localStorage !== "undefined" && localStorage.getItem("ff_device_name")) || "Owner dashboard"
  );
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user?.is_admin) return <Navigate to="/owner" replace />;
  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await loginWithEmail(email, password, deviceName || "Owner dashboard");
      if (!u?.is_admin) {
        toast.error("This account is not an owner");
        return;
      }
      navigate("/owner", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // The backend sometimes returns an object (e.g. { code: 'device_limit_reached', message: '...', devices: [...] }).
      // Surface the human message so the owner sees WHY the login failed instead of a generic "Invalid credentials".
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object") {
        msg = detail.message || detail.code || "Invalid credentials";
      } else {
        msg = err?.message || "Invalid credentials";
      }
      toast.error(msg, { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(160deg,#1F2723 0%,#2A332C 100%)" }}
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <span className="w-10 h-10 rounded-full bg-white text-[#2A332C] flex items-center justify-center text-base">🛡️</span>
          <div className="text-white">
            <div className="text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>Shop, Cal & Meal · Owner</div>
            <div className="text-[11px] text-white/60">Private dashboard</div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg" data-testid="owner-login-form">
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>Sign in</h1>
          <p className="text-sm text-[#5C6B61] mt-1">Restricted to the Shop, Cal & Meal owner account.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="ff-label">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ff-input mt-1"
                placeholder="you@freshfork.app"
                data-testid="owner-login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="ff-label">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="ff-input mt-1"
                data-testid="owner-login-password"
                autoComplete="current-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="owner-login-submit"
            className="mt-6 w-full rounded-full px-5 py-3 text-sm font-medium bg-[#2A332C] text-white hover:bg-[#1F2723] disabled:opacity-60 transition"
          >
            {busy ? "Signing in…" : "Sign in to dashboard"}
          </button>
          <p className="text-[11px] text-[#8B9690] mt-4 text-center">
            Customers should sign in at the main app, not here.
          </p>
        </form>
      </div>
    </div>
  );
}
